if (!customElements.get('td-sticky-atc')) {
  /**
   * Sticky bottom add-to-cart bar for the product page. Proxies clicks to the real
   * add-to-cart button and mirrors its state, price and visibility.
   */
  class TdStickyAtc extends HTMLElement {
    static SELECTORS = {
      button: '[data-td-sticky-atc-button]',
      label: '[data-td-sticky-atc-label]',
      spinner: '[data-td-sticky-atc-spinner]',
      price: '[data-td-sticky-atc-price]',
    };

    // `loading` and `hidden` are the real button's own state classes, mirrored rather
    // than recomputed; the other two are published to the rest of the page.
    static LOADING_CLASS = 'loading';
    static HIDDEN_CLASS = 'hidden';
    static ACTIVE_CLASS = 'sticky-bottom--active';
    static HEIGHT_PROPERTY = '--td-sticky-atc-height';
    static ROOT_MARGIN = '0px 0px 100000px 0px';

    constructor() {
      super();
      this.button = null;
      this.label = null;
      this.spinner = null;
      this.targetButton = null;
      this.defaultLabel = '';
      this.visibilityObserver = null;
      this.stateObserver = null;
      this.sizeObserver = null;
      this.editorHidden = false;
      this.outOfView = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleTargetState = this.handleTargetState.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    /**
     * Resolves the bar's elements and the real button, then starts observing it.
     */
    connectedCallback() {
      const { SELECTORS } = TdStickyAtc;

      this.button = this.querySelector(SELECTORS.button);
      this.label = this.querySelector(SELECTORS.label);
      this.spinner = this.querySelector(SELECTORS.spinner);
      this.targetButton = document.getElementById(this.dataset.tdTargetButton);

      // Without a real add-to-cart button there is nothing to proxy, so stay hidden.
      if (!this.button || !this.targetButton) return;

      this.syncPrice();

      // Keep the merchant's configured label so the sold-out mirror can restore it.
      this.defaultLabel = this.label ? this.label.textContent.trim() : '';

      this.button.addEventListener('click', this.handleClick);

      // Stretch the root far below the viewport so the button only stops intersecting
      // once it is above the viewport. A plain viewport root never fires when a jump
      // (Home key, scroll restoration, anchor) takes the button straight from above the
      // viewport to below it, both non-intersecting, which left the bar stuck on.
      this.visibilityObserver = new IntersectionObserver(this.handleVisibility, {
        rootMargin: TdStickyAtc.ROOT_MARGIN,
      });
      this.visibilityObserver.observe(this.targetButton);

      // global.js toggleAddButton() mutates the real button's disabled attribute on
      // variant change, and product-form.js drives its `loading` class and
      // aria-disabled across a submit — mirror both rather than recompute them.
      this.stateObserver = new MutationObserver(this.handleTargetState);
      // Publish the measured bar height so other fixed UI (the Smile.io launcher)
      // can clear it without hard-coding a height per title/no-title variant.
      this.sizeObserver = new ResizeObserver(this.handleResize);
      this.sizeObserver.observe(this);

      this.stateObserver.observe(this.targetButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });
      this.handleTargetState();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }
    }

    /**
     * Tears down listeners and observers and clears the state published on the body.
     */
    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.handleClick);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.stateObserver) this.stateObserver.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      document.body.style.removeProperty(TdStickyAtc.HEIGHT_PROPERTY);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      // A theme editor section reload would otherwise strand the body class, which
      // also offsets the Smile.io launcher with no bar left underneath it.
      document.body.classList.remove(TdStickyAtc.ACTIVE_CLASS);
    }

    /**
     * Forwards a click to the real add-to-cart button. Mirrors the guards
     * product-form.js applies to the real button, so a second press mid-submit
     * can't queue another add.
     */
    handleClick() {
      if (this.targetButton.hasAttribute('disabled')) return;
      if (this.targetButton.getAttribute('aria-disabled') === 'true') return;
      this.targetButton.click();
    }

    /**
     * Publishes the bar's measured height as a custom property on the body.
     */
    handleResize() {
      document.body.style.setProperty(TdStickyAtc.HEIGHT_PROPERTY, `${Math.round(this.offsetHeight)}px`);
    }

    /**
     * Tracks whether the real add-to-cart button has scrolled out of view.
     * @param {IntersectionObserverEntry[]} entries - Observer entries for the real button.
     */
    handleVisibility(entries) {
      const entry = entries[entries.length - 1];
      // The root extends far below the viewport (see ROOT_MARGIN), so a button below
      // the fold still intersects and the bar stays hidden until the shopper has
      // scrolled past it. A zero-height rect means the button isn't rendered at all.
      this.outOfView = !entry.isIntersecting && entry.boundingClientRect.height > 0;
      this.syncVisibility();
    }

    /**
     * The price slot ships empty and carries the section's data-price-sync-id, so
     * global.js refreshes it alongside the main price on variant change. Seed it
     * once from the main price here so it is populated before the first change.
     */
    syncPrice() {
      const price = this.querySelector(TdStickyAtc.SELECTORS.price);
      const syncId = this.dataset.tdPriceSyncId;
      if (!price || !syncId) return;

      const source = Array.from(document.querySelectorAll('[data-price-sync-id]')).find(
        (element) => element.dataset.priceSyncId === syncId && !this.contains(element)
      );
      if (!source) {
        price.hidden = true;
        return;
      }

      price.innerHTML = source.innerHTML;
      price.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
    }

    /**
     * Hide the bar while a merchant has the real buy buttons block selected in the
     * theme editor, so it can't sit on top of the editor's selection outline.
     * @param {CustomEvent} event - The shopify:block:select event.
     */
    handleBlockSelect(event) {
      if (!event.target.contains(this.targetButton)) return;
      this.editorHidden = true;
      this.syncVisibility();
    }

    /**
     * Shows the bar again once the buy buttons block is deselected in the theme editor.
     * @param {CustomEvent} event - The shopify:block:deselect event.
     */
    handleBlockDeselect(event) {
      if (!event.target.contains(this.targetButton)) return;
      this.editorHidden = false;
      this.syncVisibility();
    }

    /**
     * Toggles the body class that shows the bar.
     */
    syncVisibility() {
      document.body.classList.toggle(TdStickyAtc.ACTIVE_CLASS, this.outOfView && !this.editorHidden);
    }

    /**
     * Mirrors the real button's disabled, aria-disabled and loading state onto the bar.
     */
    handleTargetState() {
      const { LOADING_CLASS, HIDDEN_CLASS } = TdStickyAtc;
      const disabled = this.targetButton.hasAttribute('disabled');
      const loading = this.targetButton.classList.contains(LOADING_CLASS);
      const ariaDisabled = this.targetButton.getAttribute('aria-disabled') === 'true';

      this.button.toggleAttribute('disabled', disabled);
      this.button.classList.toggle(LOADING_CLASS, loading);
      if (ariaDisabled) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
      if (this.spinner) this.spinner.classList.toggle(HIDDEN_CLASS, !loading);

      if (!this.label) return;
      this.label.textContent = disabled ? this.soldOutLabel() : this.defaultLabel;
    }

    /**
     * Prefer the theme's translated string, but fall back to whatever the real
     * button is currently rendering (buy-buttons.liquid outputs the sold-out
     * translation into that span) so this does not hard-depend on
     * window.variantStrings surviving an upstream rename. Reads the span rather
     * than the button so the spinner markup can't leak into the label.
     * @returns {string} The sold-out label.
     */
    soldOutLabel() {
      const fromStrings = window.variantStrings && window.variantStrings.soldOut;
      if (fromStrings) return fromStrings;
      const targetLabel = this.targetButton.querySelector('span');
      const text = targetLabel ? targetLabel.textContent.trim() : '';
      return text || this.defaultLabel;
    }
  }

  customElements.define('td-sticky-atc', TdStickyAtc);
}
